import React, { useEffect, useState } from "react";
import axios from "axios";
import { Table, Form, Button, Row, Col } from "react-bootstrap";
import "./Timesheet.scss";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useGetProjectsByCollaboratorQuery } from "store/project/project.api";
import { useGetValidatedLeaveRequestsByCollaboratorQuery } from "store/conge/congeApi";
import {
  useCreateCRAMutation,
  useSendToValidatorMutation,
} from "store/cra/cra.api";
import Validations from "@pages/gestionConge/Validations/Validations";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const Timesheet = () => {
  const { monthYear } = useParams();
  const monthNames = [
    "janvier",
    "février",
    "mars",
    "avril",
    "mai",
    "juin",
    "juillet",
    "août",
    "septembre",
    "octobre",
    "novembre",
    "décembre",
  ];

  const [selectedOption, setSelectedOption] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [daysInMonth, setDaysInMonth] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [lineData, setLineData] = useState({});
  const [lineDisabled, setLineDisabled] = useState({});
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [commentaire, setCommentaire] = useState("");
  const [files, setFiles] = useState([]);
  const [createdCRAId, setCreatedCRAId] = useState(null);
  const [steps, setSteps] = useState([]);
  const [showValidation, setShowValidation] = useState(false);
  const [isEnvoyer, setIsEnvoyer] = useState(false);
  const [validateur, setValidateur] = useState("");
  const { t } = useTranslation();

  const [sendToValidator, { isLoading: sendingValidator }] =
    useSendToValidatorMutation();
  const [createCRA, { isLoading }] = useCreateCRAMutation();

  const weekDays = ["DI", "LU", "MA", "ME", "JE", "VE", "SA"];
  const currentUser = useSelector((state) => state.auth.user);

  // 🔹 API projects et congés
  const { data: projectsData } = useGetProjectsByCollaboratorQuery(
    currentUser._id
  );
  const { data: leaveRequestsData } =
    useGetValidatedLeaveRequestsByCollaboratorQuery(currentUser._id);

  const projects = projectsData?.data?.projects || [];
  const leaveRequests = leaveRequestsData?.data || [];

  // Init mois depuis l’URL
  useEffect(() => {
    if (monthYear) {
      const [monthStr, yearStr] = monthYear.split(" ");
      const monthIndex = monthNames.indexOf(monthStr.toLowerCase());
      const year = parseInt(yearStr, 10);
      if (monthIndex !== -1 && !isNaN(year)) {
        setCurrentMonth(monthIndex);
        setCurrentYear(year);
      }
    }
  }, [monthYear]);

  // Filtrer projets actifs
  useEffect(() => {
    const filtered = projects.filter((p) => {
      if (!p.startDate) return false;
      const start = new Date(p.startDate);
      const end = p.endDate ? new Date(p.endDate) : new Date();
      const currentMonthYear = currentYear * 12 + currentMonth;
      const startMonthYear = start.getFullYear() * 12 + start.getMonth();
      const endMonthYear = end.getFullYear() * 12 + end.getMonth();
      return (
        currentMonthYear >= startMonthYear && currentMonthYear <= endMonthYear
      );
    });
    setFilteredProjects(filtered);
  }, [projects, currentMonth, currentYear]);

  // Générer jours du mois + fériés
  useEffect(() => {
    const days = [];
    const date = new Date(currentYear, currentMonth, 1);
    while (date.getMonth() === currentMonth) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    setDaysInMonth(days);

    const fetchHolidays = async () => {
      try {
        const res = await axios.get(
          `https://date.nager.at/api/v3/PublicHolidays/${currentYear}/FR`
        );
        setHolidays(res.data.map((h) => new Date(h.date).toDateString()));
      } catch (error) {
        console.error(error);
      }
    };
    fetchHolidays();
  }, [currentMonth, currentYear]);

  // Remplir congés auto
  useEffect(() => {
    if (!daysInMonth.length || !leaveRequests.length) return;

    const daysStrInMonth = daysInMonth.map((d) =>
      d.toLocaleDateString("fr-CA")
    );
    const allLeaves = leaveRequests.flatMap((req) => req.leaves || []);

    const filteredPaidLeaves = allLeaves.filter(
      (l) =>
        l.type.toLowerCase() !== "non-paye" &&
        Object.keys(l.calendarNotes || {}).some((d) =>
          daysStrInMonth.includes(d)
        )
    );
    const filteredUnpaidLeaves = allLeaves.filter(
      (l) =>
        l.type.toLowerCase() === "non-paye" &&
        Object.keys(l.calendarNotes || {}).some((d) =>
          daysStrInMonth.includes(d)
        )
    );

    const fillLine = (leaves) => {
      const newLine = {};
      daysInMonth.forEach((day) => {
        const dayStr = day.toLocaleDateString("fr-CA");
        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
        const isHoliday = holidays.includes(day.toDateString());
        const hasLeave = leaves.some(
          (leave) => leave.calendarNotes && leave.calendarNotes[dayStr]
        );
        if (isWeekend || isHoliday) newLine[dayStr] = 1;
        else if (hasLeave) newLine[dayStr] = "1";
        else newLine[dayStr] = "";
      });
      return newLine;
    };

    if (filteredPaidLeaves.length > 0) {
      setLineData((prev) => ({
        ...prev,
        conge_paye: fillLine(filteredPaidLeaves),
      }));
      setLineDisabled((prev) => ({ ...prev, conge_paye: true }));
    }
    if (filteredUnpaidLeaves.length > 0) {
      setLineData((prev) => ({
        ...prev,
        conge_non_paye: fillLine(filteredUnpaidLeaves),
      }));
      setLineDisabled((prev) => ({ ...prev, conge_non_paye: true }));
    }
  }, [daysInMonth, leaveRequests, holidays]);

  // Navigation mois
  const handlePrevMonth = () => {
    let newMonth = currentMonth - 1;
    let newYear = currentYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear -= 1;
    }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };
  const handleNextMonth = () => {
    let newMonth = currentMonth + 1;
    let newYear = currentYear;
    if (newMonth > 11) {
      newMonth = 0;
      newYear += 1;
    }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  // Ajout ligne
  const handleSelectChange = (e) => {
    const value = e.target.value;
    setSelectedOption(value);
    if (!value) return;
    if (lineData[value]) {
      setLineDisabled((prev) => ({ ...prev, [value]: false }));
    } else {
      const newLine = {};
      daysInMonth.forEach((day) => {
        const dayStr = day.toLocaleDateString("fr-CA");
        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
        const isHoliday = holidays.includes(day.toDateString());
        newLine[dayStr] = isWeekend || isHoliday ? 1 : "";
      });
      setLineData((prev) => ({ ...prev, [value]: newLine }));
      setLineDisabled((prev) => ({ ...prev, [value]: false }));
    }
  };

  const handleInputChange = (lineKey, dayStr, e) => {
    const value = e.target.value;
    if (["", "0", "0.", "0.5", "1"].includes(value)) {
      setLineData((prev) => ({
        ...prev,
        [lineKey]: { ...prev[lineKey], [dayStr]: value },
      }));
    }
  };

  const handleSaveLine = (lineKey) =>
    setLineDisabled((prev) => ({ ...prev, [lineKey]: true }));
  const handleEditLine = (lineKey) =>
    setLineDisabled((prev) => ({ ...prev, [lineKey]: false }));

  // Totaux
  const calculateTotal = (lineKey) => {
    if (!lineData[lineKey]) return 0;
    return Object.entries(lineData[lineKey]).reduce((sum, [dayStr, value]) => {
      const day = new Date(dayStr);
      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
      const isHoliday = holidays.includes(day.toDateString());
      if (isWeekend || isHoliday) return sum;
      return sum + (Number(value) || 0);
    }, 0);
  };

  const calculateColumnTotal = (dayStr, isWeekend, isHoliday) => {
    if (isWeekend || isHoliday) return 1;
    return Object.keys(lineData).reduce(
      (sum, lineKey) => sum + (Number(lineData[lineKey][dayStr]) || 0),
      0
    );
  };

  const monthName = new Date(currentYear, currentMonth).toLocaleString(
    "fr-FR",
    { month: "long" }
  );

  const totalProduction = Object.keys(lineData)
    .filter(
      (k) => k !== "conge_paye" && k !== "conge_non_paye" && k !== "intern"
    )
    .reduce((sum, k) => sum + calculateTotal(k), 0);

  const totalAbsences = ["conge_paye", "conge_non_paye"].reduce(
    (sum, k) => sum + calculateTotal(k),
    0
  );

  const totalIntern = lineData["intern"] ? calculateTotal("intern") : 0;
  const totalWeekends = daysInMonth.filter(
    (d) => d.getDay() === 0 || d.getDay() === 6
  ).length;
  const totalHolidays = daysInMonth.filter((d) =>
    holidays.includes(d.toDateString())
  ).length;
  const totalDaysInMonth = daysInMonth.length;

  // Upload fichiers
  const handleFileChange = (e) => setFiles([...e.target.files]);

  // Enregistrement global
  const handleSaveCRA = async () => {
    try {
      const formData = new FormData();
      formData.append("userId", currentUser._id);
      formData.append("month", Number(currentMonth + 1));
      formData.append("year", Number(currentYear));
      formData.append("lines", JSON.stringify(lineData));
      formData.append("totalProduction", Number(totalProduction));
      formData.append("totalAbsences", Number(totalAbsences));
      formData.append("totalIntern", Number(totalIntern));
      formData.append("commentaire", commentaire);
      formData.append("supplier", currentUser.supplierId);
      formData.append("collaborator", currentUser._id);
      formData.append("validateur", currentUser.validateur);
      files.forEach((file) => formData.append("attachments", file));

      const savedCRA = await createCRA(formData).unwrap();
      toast.success(t("timesheet.cra_saved"));
      console.log(savedCRA);
      setValidateur(savedCRA?.data?.validateur.name);
      setCreatedCRAId(savedCRA.data._id);
    } catch (error) {
      console.error("Erreur CRA:", error);
      toast.error(t("timesheet.cra_save_error"));
    }
  };

  return (
    <div className="timesheet-container p-3 m-4 ">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Button size="sm" onClick={handlePrevMonth}>
            &lt;
          </Button>
          <span className="mx-2">
            {monthName} {currentYear}
          </span>
          <Button size="sm" onClick={handleNextMonth}>
            &gt;
          </Button>
        </div>
        {!createdCRAId && (
          <Button variant="primary" onClick={handleSaveCRA}>
            {t("timesheet.save")}
          </Button>
        )}
        {createdCRAId && (
          <Button
            variant="info"
            onClick={async () => {
              try {
                const response = await sendToValidator({
                  craId: createdCRAId,
                }).unwrap();
                console.log(response);
                setSteps([
                  { name: currentUser.name, date: new Date(), signed: true },
                  {
                    name: validateur,
                    date: null,
                    signed: false,
                  },
                ]);
                setShowValidation(true);
                toast.success(t("timesheet.email_sent_validator"));
              } catch (err) {
                console.error(err);
                toast.error(t("timesheet.email_send_error"));
              }
            }}
            disabled={sendingValidator}>
            {t("timesheet.send_to_validator")}
          </Button>
        )}
      </div>

      <Form.Select
        value={selectedOption}
        onChange={handleSelectChange}
        className="mb-3">
        <option value="">{t("timesheet.select_placeholder")}</option>
        <option value="conge_paye">{t("timesheet.leave_paid")}</option>
        <option value="conge_non_paye">{t("timesheet.leave_unpaid")}</option>
        <option value="intern">{t("timesheet.intern")}</option>
        {filteredProjects.map((p) => (
          <option key={p._id} value={p.title}>
            {p.title}
          </option>
        ))}
      </Form.Select>

      <Table bordered hover responsive className="timesheet-table mb-3">
        <thead>
          <tr>
            <th>{t("timesheet.day")}</th>
            {daysInMonth.map((day) => (
              <th
                key={day.toDateString()}
                className={
                  day.getDay() === 0 || day.getDay() === 6
                    ? "weekend"
                    : holidays.includes(day.toDateString())
                    ? "holiday"
                    : ""
                }>
                <div>{weekDays[day.getDay()]}</div>
                <div>{day.getDate()}</div>
              </th>
            ))}
            <th>{t("timesheet.total")}</th>
            <th>{t("timesheet.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(lineData).map((lineKey) => (
            <tr
              key={lineKey}
              className={lineDisabled[lineKey] ? "saved-line" : ""}>
              <td>{lineKey}</td>
              {daysInMonth.map((day) => {
                const dayStr = day.toLocaleDateString("fr-CA");
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const isHoliday = holidays.includes(day.toDateString());
                return (
                  <td key={dayStr}>
                    <Form.Control
                      type="text"
                      value={lineData[lineKey]?.[dayStr] ?? ""}
                      disabled={isWeekend || isHoliday || lineDisabled[lineKey]}
                      onChange={(e) => handleInputChange(lineKey, dayStr, e)}
                    />
                  </td>
                );
              })}
              <td>{calculateTotal(lineKey)}j</td>
              <td>
                {!lineDisabled[lineKey] ? (
                  <Button
                    size="sm"
                    variant="success"
                    onClick={() => handleSaveLine(lineKey)}>
                    {t("timesheet.save_line")}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={() => handleEditLine(lineKey)}>
                    {t("timesheet.edit_line")}
                  </Button>
                )}
              </td>
            </tr>
          ))}
          <tr className="column-total">
            <td>
              <strong>{t("timesheet.total")}</strong>
            </td>
            {daysInMonth.map((day) => {
              const dayStr = day.toLocaleDateString("fr-CA");
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              const isHoliday = holidays.includes(day.toDateString());
              const colTotal = calculateColumnTotal(
                dayStr,
                isWeekend,
                isHoliday
              );
              return (
                <td
                  key={dayStr}
                  style={{
                    color: colTotal > 1 ? "red" : "inherit",
                    fontWeight: "bold",
                  }}>
                  {colTotal}
                </td>
              );
            })}
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </Table>

      {/* Totaux partiels */}
      <Row className="mb-3">
        <Col>
          <div className="totals-box">
            <strong>{t("timesheet.production")}</strong>
            <div>{totalProduction}j</div>
          </div>
        </Col>
        <Col>
          <div className="totals-box">
            <strong>{t("timesheet.absences")}</strong>
            <div>{totalAbsences}j</div>
          </div>
        </Col>
        <Col>
          <div className="totals-box">
            <strong>{t("timesheet.intern")}</strong>
            <div>{totalIntern}j</div>
          </div>
        </Col>
      </Row>

      {/* Résumé global */}
      <Row className="mb-3">
        <Col>
          <div className="totals-box">
            <strong>{t("timesheet.global_summary")}</strong>
            <div>
              {t("timesheet.total_days")}: {totalDaysInMonth}
            </div>
            <div>
              {t("timesheet.production")}: {totalProduction}j
            </div>
            <div>
              {t("timesheet.absences")}: {totalAbsences}j
            </div>
            <div>
              {t("timesheet.intern")}: {totalIntern}j
            </div>
            <div>
              {t("timesheet.weekends")}: {totalWeekends}j
            </div>
            <div>
              {t("timesheet.holidays")}: {totalHolidays}j
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Form.Group>
            <Form.Label>{t("timesheet.comments")}</Form.Label>
            <Form.Control
              as="textarea"
              placeholder={t("timesheet.write_comments")}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group>
            <Form.Label>{t("timesheet.attachments")}</Form.Label>
            <div
              className="upload-dropzone"
              onClick={() => document.getElementById("fileInput").click()}>
              <input
                id="fileInput"
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div className="text-center">
                <i className="bi bi-upload" style={{ fontSize: "24px" }}></i>
                <div>{t("timesheet.upload_text")}</div>
                <small>{t("timesheet.upload_hint")}</small>
              </div>
            </div>
            <div>
              {files.map((f, i) => (
                <span key={i} className="me-2">
                  {f.name}
                </span>
              ))}
            </div>
          </Form.Group>
        </Col>
      </Row>
      {showValidation && (
        <div className="p-4">
          <Validations steps={steps} currentStep={2} />
        </div>
      )}
    </div>
  );
};

export default Timesheet;
